import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon, CopyIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  OrganizationsApi,
  RATE_LIMIT_MAX,
  RATE_LIMIT_PRESETS,
  type OrganizationDetail as OrganizationDetailT,
  type OrganizationPatch,
} from "../api/organizations";
import { fmtDate } from "../utils/datetime";
import logger from "../analytics/logger";

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function buildPatch(
  orig: OrganizationDetailT,
  draft: OrganizationDetailT,
): OrganizationPatch {
  const patch: OrganizationPatch = {};
  if (!eq(orig.name, draft.name)) patch.name = draft.name;
  if (!eq(orig.contact_name, draft.contact_name)) patch.contact_name = draft.contact_name;
  if (!eq(orig.contact_email, draft.contact_email)) patch.contact_email = draft.contact_email;
  if (!eq(orig.contact_phone, draft.contact_phone)) patch.contact_phone = draft.contact_phone;
  if (orig.verify_rate_limit_per_hour !== draft.verify_rate_limit_per_hour)
    patch.verify_rate_limit_per_hour = draft.verify_rate_limit_per_hour;
  if (orig.verify_rate_limit_per_minute !== draft.verify_rate_limit_per_minute)
    patch.verify_rate_limit_per_minute = draft.verify_rate_limit_per_minute;
  return patch;
}

export default function OrganizationDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [orig, setOrig] = useState<OrganizationDetailT | null>(null);
  const [draft, setDraft] = useState<OrganizationDetailT | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    OrganizationsApi.get(id)
      .then((d) => {
        setOrig(d);
        setDraft(d);
      })
      .catch((e) => {
        logger.error("organization_get_failed", e);
        setError(e?.response?.data?.error ?? "Failed to load organization");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancel = () => {
    if (orig) setDraft(orig);
    setEditing(false);
  };

  const back = () => {
    cancel();
    navigate("/organizations");
  };

  const save = async () => {
    if (!orig || !draft) return;
    const patch = buildPatch(orig, draft);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await OrganizationsApi.update(id, patch);
      setOrig(updated);
      setDraft(updated);
      setEditing(false);
    } catch (e: unknown) {
      logger.error("organization_update_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to save changes";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const updated = await OrganizationsApi.regenerateApiKey(id);
      setOrig(updated);
      setDraft(updated);
      setConfirmOpen(false);
      toast({
        status: "success",
        title: "API key regenerated",
        description: "Partners using the old key will start failing immediately.",
        duration: 4000,
      });
    } catch (e: unknown) {
      logger.error("organization_regenerate_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to regenerate API key";
      setError(msg);
    } finally {
      setRegenerating(false);
    }
  };

  const copyKey = async () => {
    if (!orig) return;
    try {
      await navigator.clipboard.writeText(orig.api_key);
      toast({ status: "success", title: "Copied API key", duration: 2000 });
    } catch (e) {
      logger.error("organization_copy_key_failed", e);
      toast({ status: "error", title: "Copy failed", duration: 2500 });
    }
  };

  const copyId = async () => {
    if (!orig) return;
    try {
      await navigator.clipboard.writeText(orig.id);
      toast({ status: "success", title: "Copied Organization ID", duration: 2000 });
    } catch (e) {
      logger.error("organization_copy_id_failed", e);
      toast({ status: "error", title: "Copy failed", duration: 2500 });
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (!orig || !draft) {
    return (
      <Box p={6}>
        <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={back} mb={4}>
          Back
        </Button>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>{error ?? "Organization not found"}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={back}
          />
          <Heading size="lg">{orig.name || "(unnamed)"}</Heading>
        </HStack>
        <HStack>
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={cancel} isDisabled={saving}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                Save
              </Button>
            </>
          ) : (
            <HStack>
              <Text fontSize="sm" color="gray.600">
                Edit
              </Text>
              <Switch
                isChecked={editing}
                onChange={(e) => setEditing(e.target.checked)}
              />
            </HStack>
          )}
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Stack spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Name
            </Text>
            {editing ? (
              <Input
                size="sm"
                value={draft.name ?? ""}
                onChange={(e) => setDraft({ ...draft, name: e.target.value || null })}
              />
            ) : (
              <Text>{orig.name || "—"}</Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Contact name
            </Text>
            {editing ? (
              <Input
                size="sm"
                value={draft.contact_name ?? ""}
                onChange={(e) => setDraft({ ...draft, contact_name: e.target.value || null })}
              />
            ) : (
              <Text>{orig.contact_name || "—"}</Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Contact email
            </Text>
            {editing ? (
              <Input
                size="sm"
                value={draft.contact_email ?? ""}
                onChange={(e) => setDraft({ ...draft, contact_email: e.target.value || null })}
              />
            ) : (
              <Text>{orig.contact_email || "—"}</Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Contact phone
            </Text>
            {editing ? (
              <Input
                size="sm"
                value={draft.contact_phone ?? ""}
                onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value || null })}
              />
            ) : (
              <Text>{orig.contact_phone || "—"}</Text>
            )}
          </Box>
          <Box gridColumn={{ md: "span 2" }}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Verifier API rate limits
            </Text>
            {editing ? (
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  {RATE_LIMIT_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      size="xs"
                      variant={
                        draft.verify_rate_limit_per_hour === preset.perHour &&
                        draft.verify_rate_limit_per_minute === preset.perMinute
                          ? "solid"
                          : "outline"
                      }
                      onClick={() =>
                        setDraft({
                          ...draft,
                          verify_rate_limit_per_hour: preset.perHour,
                          verify_rate_limit_per_minute: preset.perMinute,
                        })
                      }
                    >
                      {preset.label}
                    </Button>
                  ))}
                </HStack>
                <HStack spacing={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Per hour
                    </Text>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      max={RATE_LIMIT_MAX}
                      value={draft.verify_rate_limit_per_hour}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          verify_rate_limit_per_hour: Number(e.target.value),
                        })
                      }
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Per minute (burst)
                    </Text>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      max={RATE_LIMIT_MAX}
                      value={draft.verify_rate_limit_per_minute}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          verify_rate_limit_per_minute: Number(e.target.value),
                        })
                      }
                    />
                  </Box>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  0 blocks this partner without revoking their key. Per-minute cannot
                  exceed per-hour.
                </Text>
              </Box>
            ) : (
              <Text>
                {orig.verify_rate_limit_per_hour}/hour, {orig.verify_rate_limit_per_minute}/minute
                {orig.verify_rate_limit_per_hour === 0 || orig.verify_rate_limit_per_minute === 0
                  ? " — blocked"
                  : ""}
              </Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Created
            </Text>
            <Text>{fmtDate(orig.created_at)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Updated
            </Text>
            <Text>{fmtDate(orig.updated_at)}</Text>
          </Box>
        </Stack>

        <Stack spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Organization ID
            </Text>
            <HStack>
              <Code fontSize="sm" p={2} borderRadius="md" wordBreak="break-all">
                {orig.id}
              </Code>
              <Tooltip label="Copy Organization ID">
                <IconButton
                  aria-label="Copy Organization ID"
                  icon={<CopyIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={copyId}
                />
              </Tooltip>
            </HStack>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              API key
            </Text>
            <HStack>
              <Code fontSize="sm" p={2} borderRadius="md" wordBreak="break-all">
                {orig.api_key}
              </Code>
              <Tooltip label="Copy API key">
                <IconButton
                  aria-label="Copy API key"
                  icon={<CopyIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={copyKey}
                />
              </Tooltip>
            </HStack>
            <Button
              size="xs"
              mt={2}
              colorScheme="red"
              variant="outline"
              onClick={() => setConfirmOpen(true)}
            >
              Regenerate API key
            </Button>
          </Box>
        </Stack>
      </SimpleGrid>

      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Regenerate API key?
            </AlertDialogHeader>
            <AlertDialogBody>
              This invalidates the existing key. Partners using it will start failing
              immediately.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)} isDisabled={regenerating}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={regenerate} isLoading={regenerating}>
                Regenerate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
